import { renderMediaOnWeb } from '@remotion/web-renderer';
import type { Caption } from '@remotion/captions';
import { SubtitledVideo } from '../composition/SubtitledVideo';
import type { CaptionStyleId } from '../composition/captionStyles';
import type { VideoMeta } from './pipeline';
import { negotiateOutput, type OutputChoice } from './codecs';

export type ExportProgress = { progress: number; encodedFrames: number };

/** h264 rejects odd dimensions. Round down rather than letting the encoder fail. */
const even = (n: number) => Math.max(2, Math.floor(n / 2) * 2);

export const exportSubtitledVideo = async (opts: {
  src: string;
  meta: VideoMeta;
  captions: Caption[];
  styleId: CaptionStyleId;
  accent: string;
  fontFamily: string;
  /** Render only these frames. Null renders the whole composition. */
  frameRange: [number, number] | null;
  onProgress: (p: ExportProgress) => void;
  signal: AbortSignal;
}): Promise<{ blob: Blob; choice: OutputChoice; extension: string }> => {
  const choice = await negotiateOutput();

  const durationInFrames = Math.max(
    1,
    Math.floor(opts.meta.durationInSeconds * opts.meta.fps),
  );

  const inputProps = {
    src: opts.src,
    captions: opts.captions,
    styleId: opts.styleId,
    accent: opts.accent,
    fontFamily: opts.fontFamily,
    // Not negotiable: @remotion/web-renderer cannot use the fallback
    // components, so the WebCodecs tag is the only one that renders here.
    videoBackend: 'webcodecs' as const,
  };

  const result = await renderMediaOnWeb({
    composition: {
      id: 'SubtitledVideo',
      component: SubtitledVideo,
      width: even(opts.meta.width),
      height: even(opts.meta.height),
      fps: opts.meta.fps,
      durationInFrames,
      defaultProps: inputProps,
    },
    inputProps,
    container: choice.container,
    videoCodec: choice.videoCodec,
    audioCodec: choice.audioCodec,
    // The composition still sees absolute frame numbers, so captions stay
    // aligned — this only limits which frames get encoded.
    frameRange: opts.frameRange,
    hardwareAcceleration: 'prefer-hardware',
    signal: opts.signal,
    onProgress: ({ progress, encodedFrames }) =>
      opts.onProgress({ progress, encodedFrames }),
  });

  return {
    blob: await result.getBlob(),
    choice,
    extension: choice.container === 'webm' ? 'webm' : 'mp4',
  };
};
